import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { X, Plus, Trash2, Rocket, Palette, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const COLUMNS = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "qualified", label: "Qualified" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

const SOURCE_TABS = [
  { key: "all",        label: "All leads",        match: () => true },
  { key: "onboarding", label: "Onboarding requests", match: (l) => l.source === "clients_hub_onboarding" },
  { key: "chat",       label: "Chat signals",     match: (l) => typeof l.source === "string" && l.source.startsWith("chat_signal") },
  { key: "website",    label: "Website + form",   match: (l) => l.source === "website" || l.source === "form" },
];

export default function Leads() {
  const { activeTenant } = useTenant();
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("kanban"); // kanban | list
  const [tab, setTab] = useState("all"); // source filter
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "", source: "website", notes: "" });

  const load = async () => {
    if (!activeTenant) return;
    const { data } = await api.get("/leads");
    setLeads(data);
  };

  useEffect(() => {
    load();
  }, [activeTenant?.id]);

  const activeTabDef = SOURCE_TABS.find((t) => t.key === tab) || SOURCE_TABS[0];
  const filteredLeads = useMemo(() => leads.filter(activeTabDef.match), [leads, activeTabDef]);
  const tabCounts = useMemo(
    () => SOURCE_TABS.reduce((acc, t) => ({ ...acc, [t.key]: leads.filter(t.match).length }), {}),
    [leads]
  );

  const createLead = async (e) => {
    e.preventDefault();
    await api.post("/leads", draft);
    setOpenCreate(false);
    setDraft({ name: "", email: "", phone: "", source: "website", notes: "" });
    load();
  };

  const updateStatus = async (lead, status) => {
    await api.patch(`/leads/${lead.id}`, { status });
    load();
  };

  const deleteLead = async (id) => {
    await api.delete(`/leads/${id}`);
    setSelected(null);
    load();
  };

  const [provisioning, setProvisioning] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null); // {ok, tenant?, error?}

  const provisionFromLead = async (lead) => {
    if (!lead) return;
    setProvisioning(true);
    setProvisionResult(null);
    try {
      const { data } = await api.post(`/leads/${lead.id}/provision`);
      setProvisionResult({ ok: true, tenant: data.tenant });
      // Refresh both the list AND the selected drawer view
      await load();
      const refreshed = { ...lead, status: "won", fields: { ...(lead.fields || {}), provisioned_tenant_id: data.tenant.id, provisioned_slug: data.tenant.slug } };
      setSelected(refreshed);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Provisioning failed. Try again.";
      setProvisionResult({ ok: false, error: msg });
    } finally {
      setProvisioning(false);
    }
  };

  if (!activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Select a tenant.</div>;

  return (
    <div className="p-8" data-testid="leads-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">CRM · {activeTenant.name}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Leads pipeline</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-[var(--n-border)]">
            <button
              onClick={() => setView("kanban")}
              className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-wider ${view === "kanban" ? "bg-white text-black" : "text-[var(--n-text-sec)]"}`}
              data-testid="view-kanban"
            >
              Kanban
            </button>
            <button
              onClick={() => setView("list")}
              className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-wider ${view === "list" ? "bg-white text-black" : "text-[var(--n-text-sec)]"}`}
              data-testid="view-list"
            >
              List
            </button>
          </div>
          <button
            className="n-btn flex items-center gap-2"
            onClick={() => setOpenCreate(true)}
            data-testid="new-lead-button"
          >
            <Plus size={14} /> New Lead
          </button>
        </div>
      </div>

      {/* Source filter tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="leads-source-tabs">
        {SOURCE_TABS.map((t) => {
          const active = tab === t.key;
          const count = tabCounts[t.key] || 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-xs font-mono-plex uppercase tracking-wider border transition-colors flex items-center gap-2 ${active ? "bg-white text-black border-white" : "border-[var(--n-border)] text-[var(--n-text-sec)] hover:text-white"}`}
              data-testid={`leads-tab-${t.key}`}
            >
              <span>{t.label}</span>
              <span className={`text-[10px] font-mono-plex px-1.5 py-0.5 rounded ${active ? "bg-black text-white" : "bg-[var(--n-border)] text-[var(--n-text-muted)]"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {view === "kanban" ? (
        <div className="grid grid-cols-5 gap-3" data-testid="lead-kanban-board">
          {COLUMNS.map((c) => {
            const items = filteredLeads.filter((l) => l.status === c.id);
            return (
              <div key={c.id} className="kanban-col p-3" data-testid={`kanban-col-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="n-label">{c.label}</div>
                  <span className="text-xs font-mono-plex text-[var(--n-text-muted)]">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((l) => (
                    <div
                      key={l.id}
                      className="lead-card p-3"
                      onClick={() => setSelected(l)}
                      data-testid={`lead-card-${l.id}`}
                    >
                      <div className="font-cab font-bold text-sm">{l.name}</div>
                      {l.email && <div className="text-xs text-[var(--n-text-sec)] mt-0.5 truncate">{l.email}</div>}
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-[10px] font-mono-plex text-[var(--n-text-muted)] uppercase">
                          {l.source}
                        </span>
                        <select
                          value={l.status}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => updateStatus(l, e.target.value)}
                          className="bg-transparent text-[10px] font-mono-plex border border-[var(--n-border)] px-1 py-0.5 text-[var(--n-text-sec)] uppercase"
                          data-testid={`lead-status-${l.id}`}
                        >
                          {COLUMNS.map((cc) => (
                            <option key={cc.id} value={cc.id}>
                              {cc.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && (
                    <div className="text-xs text-[var(--n-text-muted)] py-6 text-center font-mono-plex">
                      empty
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="n-card overflow-hidden" data-testid="leads-list">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a] border-b border-[var(--n-border)]">
              <tr className="text-left">
                {["Name", "Email", "Phone", "Source", "Status", "Created"].map((h) => (
                  <th key={h} className="n-label py-2 px-4">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((l) => (
                <tr
                  key={l.id}
                  onClick={() => setSelected(l)}
                  className="border-b border-[var(--n-border)] hover:bg-[#101015] cursor-pointer"
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="px-4 py-3 font-cab font-medium">{l.name}</td>
                  <td className="px-4 py-3 text-[var(--n-text-sec)]">{l.email || "—"}</td>
                  <td className="px-4 py-3 text-[var(--n-text-sec)] font-mono-plex text-xs">{l.phone || "—"}</td>
                  <td className="px-4 py-3"><span className="n-chip">{l.source}</span></td>
                  <td className="px-4 py-3"><span className="n-chip">{l.status}</span></td>
                  <td className="px-4 py-3 text-xs font-mono-plex text-[var(--n-text-muted)]">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {filteredLeads.length === 0 && (
                <tr><td colSpan={6} className="py-10 text-center text-[var(--n-text-muted)]">No leads in this view yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {openCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setOpenCreate(false)}>
          <form
            onSubmit={createLead}
            onClick={(e) => e.stopPropagation()}
            className="n-card w-full max-w-md p-6"
            data-testid="create-lead-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-cab font-black text-xl">New lead</div>
              <button type="button" onClick={() => setOpenCreate(false)}><X size={18} /></button>
            </div>
            {["name", "email", "phone"].map((k) => (
              <div key={k} className="mb-3">
                <label className="n-label block mb-1">{k}</label>
                <input
                  className="n-input"
                  value={draft[k]}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  required={k === "name"}
                  data-testid={`new-lead-${k}`}
                />
              </div>
            ))}
            <div className="mb-3">
              <label className="n-label block mb-1">source</label>
              <select
                className="n-input"
                value={draft.source}
                onChange={(e) => setDraft({ ...draft, source: e.target.value })}
                data-testid="new-lead-source"
              >
                {["website", "referral", "google_ads", "facebook", "manual"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="mb-4">
              <label className="n-label block mb-1">notes</label>
              <textarea
                rows={3}
                className="n-input"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                data-testid="new-lead-notes"
              />
            </div>
            <button className="n-btn w-full" type="submit" data-testid="create-lead-submit">CREATE LEAD</button>
          </form>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/70 z-50" onClick={() => setSelected(null)} data-testid="lead-detail-drawer">
          <div
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[var(--n-paper)] border-l border-[var(--n-border)] p-6 overflow-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="n-label">Lead detail</div>
              <button onClick={() => setSelected(null)}><X size={18} /></button>
            </div>
            <div className="font-cab font-black text-3xl tracking-tight">{selected.name}</div>
            <div className="text-sm text-[var(--n-text-sec)] mt-1">{selected.email || "—"}</div>
            <div className="text-sm font-mono-plex text-[var(--n-text-sec)] mt-0.5">{selected.phone || "—"}</div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="border border-[var(--n-border)] p-3">
                <div className="n-label">Source</div>
                <div className="font-cab font-bold text-sm mt-1">{selected.source}</div>
              </div>
              <div className="border border-[var(--n-border)] p-3">
                <div className="n-label">Status</div>
                <select
                  value={selected.status}
                  onChange={async (e) => {
                    await updateStatus(selected, e.target.value);
                    setSelected({ ...selected, status: e.target.value });
                  }}
                  className="bg-transparent font-cab font-bold text-sm mt-1 w-full"
                  data-testid="lead-detail-status"
                >
                  {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <div className="n-label mb-2">Notes</div>
              <div className="text-sm text-[var(--n-text-sec)] whitespace-pre-wrap border border-[var(--n-border)] p-3 min-h-[80px]">
                {selected.notes || "—"}
              </div>
            </div>

            {selected.source === "clients_hub_onboarding" && selected.fields && (
              <div className="mt-6 border border-[var(--n-accent-yellow,#F5B800)] p-4" data-testid="lead-onboarding-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Rocket size={14} style={{ color: "#F5B800" }} />
                  <div className="n-label" style={{ color: "#F5B800" }}>Onboarding request · fields</div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <OnboardingField label="Company" value={selected.fields.company_name} />
                  <OnboardingField label="Industry" value={selected.fields.industry} />
                  <OnboardingField label="Website" value={selected.fields.website} link />
                  <OnboardingField label="Budget" value={selected.fields.monthly_budget} />
                  <OnboardingField label="Launch" value={selected.fields.launch_target} />
                  <OnboardingField
                    label="Brand color"
                    value={selected.fields.primary_color}
                    swatch={selected.fields.primary_color}
                  />
                </div>
                {Array.isArray(selected.fields.modules) && selected.fields.modules.length > 0 && (
                  <div className="mt-4">
                    <div className="n-label mb-2 flex items-center gap-2">
                      <Palette size={12} /> Requested modules
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.fields.modules.map((m) => (
                        <span key={m} className="n-chip text-[10px]">{m}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Provision from lead — one-click tenant spin-up */}
                <div className="mt-5 pt-5 border-t border-[var(--n-border)]">
                  {selected.fields.provisioned_tenant_id ? (
                    <div
                      className="flex items-center gap-2 text-xs p-3 border rounded"
                      style={{ borderColor: "#22C55E55", color: "#22C55E", background: "rgba(34,197,94,0.06)" }}
                      data-testid="lead-provisioned-banner"
                    >
                      <CheckCircle2 size={14} />
                      <span>
                        Provisioned as tenant <span className="font-black">{selected.fields.provisioned_slug || selected.fields.provisioned_tenant_id}</span>
                      </span>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => provisionFromLead(selected)}
                        disabled={provisioning}
                        className="w-full h-11 rounded font-black uppercase tracking-[0.22em] text-[11px] inline-flex items-center justify-center gap-2 transition-colors"
                        style={{
                          background: "#F5B800",
                          color: "#0A0A0C",
                          opacity: provisioning ? 0.6 : 1,
                          boxShadow: "0 14px 30px -14px rgba(245,184,0,0.6)",
                        }}
                        data-testid="lead-provision-btn"
                      >
                        {provisioning ? (
                          <>
                            <Loader2 size={14} className="animate-spin" /> Provisioning…
                          </>
                        ) : (
                          <>
                            <Rocket size={14} /> Provision tenant now
                          </>
                        )}
                      </button>
                      <div className="text-[10px] uppercase tracking-[0.22em] mt-2 text-[var(--n-text-muted)]">
                        Creates tenant + site + modules from these fields. Owner permission required.
                      </div>
                      {provisionResult?.ok && (
                        <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "#22C55E" }} data-testid="lead-provision-ok">
                          <CheckCircle2 size={14} /> Tenant <span className="font-black">{provisionResult.tenant.slug}</span> is live.
                        </div>
                      )}
                      {provisionResult?.ok === false && (
                        <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "#F87171" }} data-testid="lead-provision-error">
                          <AlertTriangle size={14} /> {provisionResult.error}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => deleteLead(selected.id)}
              className="mt-8 flex items-center gap-2 text-sm text-[var(--n-danger)]"
              data-testid="lead-delete-button"
            >
              <Trash2 size={14} /> Delete lead
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OnboardingField({ label, value, link, swatch }) {
  if (!value) {
    return (
      <>
        <div className="n-label">{label}</div>
        <div className="text-[var(--n-text-muted)]">—</div>
      </>
    );
  }
  return (
    <>
      <div className="n-label">{label}</div>
      <div className="flex items-center gap-2 text-[var(--n-text)] font-mono-plex text-xs">
        {swatch && (
          <span
            className="w-3.5 h-3.5 rounded-sm inline-block border border-[var(--n-border)]"
            style={{ background: swatch }}
          />
        )}
        {link ? (
          <a href={value} target="_blank" rel="noreferrer" className="hover:text-[var(--n-accent-yellow,#F5B800)] break-all">
            {value}
          </a>
        ) : (
          <span className="break-all">{value}</span>
        )}
      </div>
    </>
  );
}
